import React, { Fragment, useMemo, useState } from "react";
import { FileDragUploadBackdrop } from "../../../../common/component/file-drag-upload/file-drag-upload-backdrop";
import { classNamesList } from "../../../../common/utils/class-name";
import { Button, TextField, Typography } from "@mui/material";
import { BtmShippingInfo } from "../model/btm-shipping-info";
import { IUploadedFileDto } from "../../../../common/model/file-exchange-dto";
import { FileToExchangeDto } from "../../../../helpers/file-to-exchange-dto";
import { FileDragUpload } from "../../../../common/component/file-drag-upload/file-drag-upload";
import { Add, Delete, Download, Upload } from "@mui/icons-material";
import FileSaver from "file-saver";
import { FileExchangeToFile } from "../../../../helpers/file-exchange-to-file";
import { t } from "i18next";
import { notificationModel } from "../../../../common/component/notifications/notification-model";

import "./style.scss";

interface IBtmRequestTrackingManagementProps {
    shippingInfo: BtmShippingInfo[];
    isDisabled: boolean;
    onTrackingAdded: () => void;
    onShippingInfoChange: (index: number, shippingInfo: BtmShippingInfo) => void;
}

export const BtmRequestTrackingManagement = (props: IBtmRequestTrackingManagementProps) => {
    const [isFileDragging, setIsFileDragging] = useState(false);
    const shippingInfo = useMemo(() => props.isDisabled ?
            [new BtmShippingInfo({})] : props.shippingInfo,
        [props.isDisabled, props.shippingInfo]);

    return <Fragment>
        <FileDragUploadBackdrop onFileDragging={setIsFileDragging} />
        <div className={"btm-tracking-management"}>
            <div className={"btm-tracking-management__list"}>
                {shippingInfo.map((shipping, index) => <div className={classNamesList([
                    "btm-request-form__row--multi", "btm-tracking-management__upload",
                    isFileDragging ? "btm-tracking-management__upload-active" : "",
                ])} key={`tracking-${index}`}>
                    <div className={"btm-tracking-management__tracking"}>
                        <Typography variant={"h5"} className={"btm-request-form__label"} color={"textSecondary"}>
                            {index === 0 ?
                                t("components.blueBrandReturns.page.details.form.tracking.label") :
                                t("components.blueBrandReturns.page.details.form.tracking.labelNext", {
                                    number: index + 1,
                                })}
                        </Typography>
                        <TextField value={shipping.tracking ?? ""} onChange={event =>
                            props.onShippingInfoChange(index, shipping.setTracking(event.target.value))}
                                   disabled={props.isDisabled} />
                    </div>
                    <BtmTrackingFileUpload isDragging={isFileDragging}
                                           isDisabled={props.isDisabled}
                                           uploadedFile={shipping.file}
                                           uploadLabelText={shipping.tracking ?
                                               t("components.blueBrandReturns.page.details.form.tracking.dropForTrackingWithValue", {
                                                   tracking: shipping.tracking,
                                               }) :
                                               t("components.blueBrandReturns.page.details.form.tracking.dropForTrackingWithNumber", {
                                                   number: index + 1,
                                               })}
                                           onFileUploaded={file =>
                                               props.onShippingInfoChange(index, (shipping.setFile(file)))} />
                </div>)}
            </div>
            <Button endIcon={<Add />} onClick={props.onTrackingAdded} size={"small"} color={"secondary"} disabled={props.isDisabled}>
                {t("components.blueBrandReturns.page.details.form.tracking.addNextLabel")}
            </Button>
        </div>
    </Fragment>;
};

const BtmTrackingFileUpload = (
    { isDragging, isDisabled, uploadLabelText, uploadedFile, onFileUploaded }:
    { isDragging: boolean, isDisabled: boolean, uploadLabelText: string, uploadedFile?: IUploadedFileDto, onFileUploaded: (file?: IUploadedFileDto) => void },
) => {
    const handleFileUploaded = async (file: File) => {
        const header = await file.slice(0, 5).text();

        if (header !== "%PDF-") {
            notificationModel.addErrorMessage(t("components.blueBrandReturns.page.details.form.tracking.invalidFileType"));

            return;
        }

        onFileUploaded(await FileToExchangeDto(file));
    };

    const handleFileDownload = async () => {
        if (uploadedFile) {
            FileSaver.saveAs(await FileExchangeToFile(uploadedFile));
        }
    };

    const handleFileDelete = () => {
        onFileUploaded(undefined);
    };

    return <FileDragUpload className={"btm-tracking-management__file"}
                           acceptedFiles={"application/pdf"}
                           isDragging={isDragging}
                           disableOnClick={uploadedFile !== undefined || isDisabled}
                           onFileUploaded={handleFileUploaded}>
        {uploadedFile && !isDragging ?
            <div className={"btm-tracking-management__file-download"}>
                <Button variant={"outlined"} color={"info"} onClick={handleFileDownload}
                        className={"btm-tracking-management__upload-button"}><Download /></Button>
                <Button variant={"contained"} color={"error"} onClick={handleFileDelete}
                        disabled={isDisabled}><Delete /></Button>
            </div> :
            !isDragging ?
                <Button disabled={isDisabled} variant={"outlined"} color={"info"} className={"btm-tracking-management__upload-button"}>
                    <span>{t("components.blueBrandReturns.page.details.form.tracking.uploadLabel")}</span>
                    <Upload />
                </Button> :
                <Typography>
                    {uploadLabelText}
                </Typography>}
    </FileDragUpload>;
};
